/* eslint-disable */
// ─── School's answer to a booking request (/respond/<token>) ──────────────────
// Opened from the Accept / Decline buttons in a booking-request email. No
// login: the token in the address is the only authorisation, checked by the
// booking-request Edge Function.
//
// Loading this page changes nothing. Mail scanners open every link in an
// email, so the decision always takes a click here — ?a=accept only decides
// which option is pre-selected.
import { useEffect, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "./security/supabaseClient";

const INK = "#2E2A24", BODY = "#4a4a5a", MUTED = "#6b6b7a", T = "#B5703F", GREEN = "#27AE60", RED = "#C0392B";
const fmtD = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

const call = async (body) => {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/booking-request`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok && data?.ok, status: res.status, data };
};

export default function RespondPage({ token }) {
  const initial = (() => { try { return new URLSearchParams(window.location.search).get("a") === "decline" ? "decline" : "accept"; } catch { return "accept"; } })();
  const [state, setState] = useState("loading");   // loading | ready | invalid | error | done
  const [req, setReq] = useState(null);
  const [decision, setDecision] = useState(initial);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);       // { status, emailed }

  useEffect(() => {
    document.title = "Booking request · YogaStay";
    call({ action: "view", token }).then(({ ok, status, data }) => {
      if (ok) { setReq(data.request); setState("ready"); }
      else { setError(data?.error || null); setState(status === 404 ? "invalid" : "error"); }
    }).catch(() => setState("error"));
  }, [token]);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const { ok, status, data } = await call({ action: "respond", token, decision, note: note.trim() });
      if (ok) { setResult({ status: data.status, emailed: data.traveller_emailed !== false }); setState("done"); }
      else if (status === 409) { setReq(r => ({ ...r, status: data.status || r.status })); setError(data?.error); }
      else setError(data?.error || "We could not save your answer. Please try again.");
    } catch { setError("We could not reach YogaStay. Check your connection and try again."); }
    setSaving(false);
  };

  const card = { background: "white", border: "1px solid rgba(181,112,63,0.18)", borderRadius: 12, padding: "20px 22px", marginBottom: 16 };
  const page = (children) => (
    <div style={{ minHeight: "100vh", background: "#FBF8F3", fontFamily: "'Jost',sans-serif", padding: "0 16px" }}>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "36px 0 60px" }}>
        <a href="/" style={{ color: T, textDecoration: "none", fontSize: 13 }}>← YogaStay</a>
        {children}
        <p style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.7, marginTop: 20 }}>
          No payment is taken on YogaStay — you collect payment from the traveller directly. <a href="/help/schools" style={{ color: T }}>How booking requests work</a> · <a href="mailto:support@yogastay.app" style={{ color: T }}>support@yogastay.app</a>
        </p>
      </div>
    </div>
  );
  const h1 = (t) => <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 34, fontWeight: 300, color: INK, margin: "18px 0 14px" }}>{t}</h1>;

  if (state === "loading") return page(<p style={{ color: MUTED, marginTop: 30 }}>Loading the request…</p>);
  if (state === "invalid") return page(<>{h1("This link is not valid")}<p style={{ color: BODY, lineHeight: 1.7 }}>{error || "Please use the buttons in your latest booking request email."}</p></>);
  if (state === "error")   return page(<>{h1("Something went wrong")}<p style={{ color: BODY, lineHeight: 1.7 }}>We could not load this request. Please refresh the page, or write to support@yogastay.app.</p></>);

  if (state === "done") {
    const yes = result.status === "confirmed";
    return page(<>
      {h1(yes ? "Request accepted" : "Request declined")}
      <div style={{ ...card, borderColor: yes ? "rgba(39,174,96,0.3)" : "rgba(0,0,0,0.1)" }}>
        <p style={{ fontSize: 15, color: BODY, lineHeight: 1.75, margin: 0 }}>
          {result.emailed
            ? <>We've emailed <strong>{req.traveller_name}</strong> to let them know{yes && note.trim() ? ", with your payment details" : ""}.</>
            : <>Your answer is saved, but we couldn't email <strong>{req.traveller_name}</strong>. Please contact them directly.</>}
          {yes ? " Their replies come straight to you. Payment, cancellation and refunds are between you and the traveller." : " Thank you for letting them know quickly."}
        </p>
      </div>
    </>);
  }

  // ready
  const answered = req.status !== "pending";
  const dates = req.start_date ? (req.end_date && req.end_date !== req.start_date ? `${fmtD(req.start_date)} – ${fmtD(req.end_date)}` : fmtD(req.start_date)) : "Flexible / not specified";
  const rows = [["Programme / room", req.listing_title], ["Dates", dates], ["Guests", String(req.guests)], ["Listed price", req.price_display ? `${req.price_display} (paid directly to you)` : ""], ["From", `${req.traveller_name}${req.traveller_country ? ` · ${req.traveller_country}` : ""}`], ["Reference", `#${req.ref}`]].filter(([, v]) => v);
  const opt = (value, label, sub, color) => (
    <label style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "13px 15px", border: `2px solid ${decision === value ? color : "rgba(0,0,0,0.1)"}`, background: decision === value ? (value === "accept" ? "rgba(39,174,96,0.06)" : "rgba(192,57,43,0.04)") : "white", borderRadius: 10, cursor: "pointer", flex: "1 1 220px" }}>
      <input type="radio" name="decision" checked={decision === value} onChange={() => setDecision(value)} style={{ marginTop: 3, accentColor: color }} />
      <span><span style={{ display: "block", fontSize: 15, fontWeight: 600, color: INK }}>{label}</span><span style={{ fontSize: 12.5, color: MUTED }}>{sub}</span></span>
    </label>
  );

  return page(<>
    {h1(`Booking request for ${req.recipient_name || "your school"}`)}
    <div style={card}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "flex", gap: 12, padding: "7px 0", borderBottom: "1px solid rgba(181,112,63,0.08)", fontSize: 14 }}>
          <span style={{ color: MUTED, width: 140, flexShrink: 0 }}>{k}</span><span style={{ color: INK, fontWeight: 500 }}>{v}</span>
        </div>
      ))}
      {req.message && <div style={{ marginTop: 12, background: "#f8f9ff", borderRadius: 8, padding: "10px 13px", fontSize: 14, color: BODY, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{req.message}</div>}
    </div>

    {answered ? (
      <div style={{ ...card, borderColor: req.status === "confirmed" ? "rgba(39,174,96,0.3)" : "rgba(192,57,43,0.25)" }}>
        <p style={{ margin: 0, fontSize: 15, color: BODY, lineHeight: 1.7 }}>
          You already <strong style={{ color: req.status === "confirmed" ? GREEN : RED }}>{req.status === "confirmed" ? "accepted" : "declined"}</strong> this request
          {req.responded_at ? ` on ${new Date(req.responded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}, and the traveller was told.
          To change it, contact the traveller directly or write to support@yogastay.app.
        </p>
      </div>
    ) : (
      <div style={card}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          {opt("accept", "Accept", "I can take this booking", GREEN)}
          {opt("decline", "Decline", "I can't take it", RED)}
        </div>
        <label style={{ display: "block", fontSize: 12, letterSpacing: 1.2, textTransform: "uppercase", color: MUTED, marginBottom: 6 }}>
          {decision === "accept" ? "Payment details and next steps for the traveller (optional)" : "Message to the traveller (optional)"}
        </label>
        <textarea rows={5} maxLength={3000} value={note} onChange={e => setNote(e.target.value)}
          placeholder={decision === "accept"
            ? "e.g. To secure your place, please pay a 30% deposit to our school account: … The balance is due on arrival. Our cancellation policy: …"
            : "e.g. We are fully booked on those dates, but have space from 12 March."}
          style={{ width: "100%", padding: "11px 13px", border: "1.5px solid rgba(181,112,63,0.3)", borderRadius: 8, fontFamily: "'Jost',sans-serif", fontSize: 14, color: INK, boxSizing: "border-box", resize: "vertical" }} />
        <p style={{ fontSize: 12, color: MUTED, margin: "6px 0 14px", lineHeight: 1.6 }}>
          {decision === "accept"
            ? "Sent to the traveller as you write it. Ask for payment into your school's own account, and include your cancellation policy."
            : "A kind note or an alternative date helps the traveller — and often still wins the booking."}
        </p>
        {error && <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "10px 13px", marginBottom: 12, fontSize: 13, color: RED }}>{error}</div>}
        <button onClick={submit} disabled={saving}
          style={{ width: "100%", padding: 15, border: "none", borderRadius: 9, cursor: saving ? "wait" : "pointer", fontFamily: "'Jost',sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: "white", background: saving ? "#aaa" : decision === "accept" ? GREEN : INK, minHeight: 50 }}>
          {saving ? "Saving…" : decision === "accept" ? "Accept and notify the traveller" : "Decline and notify the traveller"}
        </button>
      </div>
    )}
  </>);
}
